import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { api } from '../lib/api';
import './Settings.css';

interface UserData {
  id: number;
  name: string | null;
  phone: string | null;
  role: number;
  org_id: number | null;
  org_name: string;
  user_id: string;
}

const getRoleName = (role: number): string => {
  switch (role) {
    case 1:
      return 'Usuario';
    case 2:
      return 'Administrador de la Empresa';
    case 4:
      return 'Administrador del sitio';
    default:
      return 'Usuario';
  }
};

function Settings() {
  const [userData, setUserData] = useState<UserData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isPreflighting, setIsPreflighting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });

  useEffect(() => {
    fetchUserData();
  }, []);

  const fetchUserData = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) return;

      // First try to get existing user data
      let { data, error } = await supabase
        .from('user_data')
        .select(`
          *,
          org:org_id (
            name
          )
        `)
        .eq('user_id', session.user.id)
        .maybeSingle();

      if (error) throw error;
      if (!data) throw new Error('No data returned');

      setUserData({
        id: data.id,
        name: data.name,
        phone: data.phone,
        role: data.role,
        org_id: data.org_id,
        user_id: data.user_id,
        org_name: data.org?.name || 'Sin organización'
      });
    } catch (error) {
      console.error('Error fetching user data:', error);
      setMessage({ text: 'Error al cargar los datos', type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userData) return;

    setIsPreflighting(true);
    setIsSaving(false);
    setMessage({ text: '', type: '' });

    try {
      // Check session validity before request
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        window.location.href = '/';
        return;
      }

      // Log raw user data for debugging
      console.log('Raw userData:', {
        name: userData.name,
        phone: userData.phone,
        types: {
          name: typeof userData.name,
          phone: typeof userData.phone
        }
      });

      // Prepare update data with type validation and undefined handling
      const updateData = {
        ...(userData.name !== null && typeof userData.name === 'string' && userData.name.trim() !== '' && { 
          name: userData.name.trim() 
        }),
        ...(userData.phone !== null && typeof userData.phone === 'string' && userData.phone.trim() !== '' && { 
          phone: userData.phone.trim() 
        })
      };

      // Check if there's data to update
      if (Object.keys(updateData).length === 0) {
        setMessage({ text: 'No hay datos para actualizar', type: 'error' });
        return;
      }

      // Validate data types and constraints
      if (updateData.name && (updateData.name.length < 2 || updateData.name.length > 100)) {
        setMessage({ text: 'El nombre debe tener entre 2 y 100 caracteres', type: 'error' });
        return;
      }

      if (updateData.phone && !/^\+?[\d\s-]{8,20}$/.test(updateData.phone)) {
        setMessage({ text: 'El teléfono debe tener un formato válido', type: 'error' });
        return;
      }

      // Log request details
      console.log('Sending update request:', {
        url: '/api/users/update_data',
        data: updateData,
        rawData: JSON.stringify(updateData),
        dataLength: Object.keys(updateData).length,
        session: {
          user: session.user.email,
          expires_at: session.expires_at
        }
      });

      setIsPreflighting(false);
      setIsSaving(true);

      const response = await api.put('/api/users/update_data', updateData);

      // Log the complete response for debugging
      console.log('Complete response:', response);

      // Handle different status codes explicitly
      switch (response.statusCode) {
        case 401:
          window.location.href = '/';
          return;
        case 403:
          throw new Error('No tienes permisos para realizar esta acción');
        case 400:
          console.error('Bad Request Details:', response);
          throw new Error(response.detail || response.error || 'Datos inválidos');
        case 500:
          throw new Error('Error interno del servidor');
      }

      if (!response.ok) {
        console.error('Error response:', response);
        throw new Error(response.error || 'Error al actualizar los datos');
      }

      setMessage({ text: 'Cambios guardados exitosamente', type: 'success' });
      // Refresh data after successful update
      await fetchUserData();
    } catch (error) {
      console.error('Error updating user data:', error);
      if (error instanceof Error) {
        console.error('Error details:', {
          message: error.message,
          stack: error.stack
        });
      }
      setMessage({ 
        text: error instanceof Error ? error.message : 'Error al guardar los cambios', 
        type: 'error' 
      });
    } finally {
      setIsPreflighting(false);
      setIsSaving(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!userData) return;
    setUserData({
      ...userData,
      [e.target.name]: e.target.value
    });
  };

  if (isLoading) {
    return (
      <div className="settings-loading">
        <div className="loading-spinner"></div>
        <p>Cargando...</p>
      </div>
    );
  }

  return (
    <div className="settings-container">
      <h1>Configuración de Usuario</h1>
      {message.text && (
        <div className={`message ${message.type === 'error' ? 'error' : 'success'}`}>
          {message.text}
        </div>
      )}
      <form onSubmit={handleSubmit} className="settings-form">
        <div className="form-group">
          <label>Nombre:</label>
          <input
            type="text"
            name="name"
            value={userData?.name || ''}
            onChange={handleChange}
            placeholder="Tu nombre"
          />
        </div>

        <div className="form-group">
          <label>Teléfono:</label>
          <input
            type="tel"
            name="phone"
            value={userData?.phone || ''}
            onChange={handleChange}
            placeholder="Tu teléfono"
          />
        </div>

        <div className="form-group">
          <label>Rol:</label>
          <input
            type="text"
            value={userData ? getRoleName(userData.role) : ''}
            disabled
            className="readonly-input"
          />
        </div>

        <div className="form-group">
          <label>Organización:</label>
          <input
            type="text"
            value={userData?.org_name || ''}
            disabled
            className="readonly-input"
          />
        </div>

        <button 
          type="submit" 
          disabled={isPreflighting || isSaving}
          className="primary-button w-full"
        >
          {isPreflighting ? 'Verificando...' : 
           isSaving ? 'Guardando...' : 
           'Guardar Cambios'}
        </button>
      </form>
    </div>
  );
}

export default Settings; 